import React from 'react';
import BackgroundVideoVideo from './BackgroundVideoVideo';
import {Plugins, Atom} from '@packages/systems/wfdl';
import {getChildren, getInPojo} from '@packages/utilities/data-utils';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {createIntegratedRootAliasInstance} from '@packages/utilities/integrated-types';
import {usePreviewMode} from '@packages/systems/render';

const VIDEO_URLS_PATH = ['bgvideo', 'video_urls'];
const POSTER_IMAGE_PATH = ['bgvideo', 'poster_image_url'];
const emptyArray: string[] = [];

const BackgroundVideoWrapper = Atom.createRender(
  ({
    data,
    elementId,
    attributes,
    classes,
    generateAttributes,
    renderChild,
    context,
  }) => {
    // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
    const Tag = data.tag;
    const previewMode = usePreviewMode();

    // Background videos need to move video/poster URLs to data-attribute values
    // This fixes up videos that were not correctly saved before transcoding completed, and corrects
    // the published URLs for bg videos on duplicated sites
    // ".mp4" before ".webm" for accelerated playback and fewer bytes served
    // https://github.com/webflow/webflow/issues/21318
    const urls = getInPojo(data, VIDEO_URLS_PATH) as string[] | undefined;
    const videoURLs: string[] =
      !urls || urls.length === 0 ? emptyArray : urls.slice().sort();

    const backgroundVideoWrapperPoster =
      (getInPojo(data, POSTER_IMAGE_PATH) as string) || null;

    const backgroundVideoWrapperAttributes = attributes
      .set('data-video-urls', videoURLs.join(',') || '')
      // @ts-expect-error - Unknown
      .set('data-poster-url', backgroundVideoWrapperPoster);

    const videoStyle = React.useMemo(
      () =>
        backgroundVideoWrapperPoster
          ? {backgroundImage: `url("${backgroundVideoWrapperPoster}")`}
          : null,
      [backgroundVideoWrapperPoster]
    );

    const backgroundVideoWrapperClasses = classes
      .push('w-background-video')
      .push('w-background-video-atom');

    const autoPlay = attributes.get('data-autoplay');

    const loop = attributes.get('data-loop');
    const videoId = `${elementId}-video`;
    const updatedContext = context
      .set('videoId', videoId)
      .set('posterUrl', backgroundVideoWrapperPoster);

    return (
      <Tag
        {...generateAttributes(
          backgroundVideoWrapperAttributes,
          backgroundVideoWrapperClasses
        )}
      >
        <BackgroundVideoVideo
          id={videoId}
          autoPlay={autoPlay}
          loop={loop}
          previewMode={previewMode}
          videoStyle={videoStyle}
          videoURLs={videoURLs}
        />
        {getChildren(data).map((child) => renderChild(child, updatedContext))}
      </Tag>
    );
  }
);

const config = Atom.create({
  displayName: 'Background Video',
  showEmpty: false,
  icon: {
    value: qualify(
      Plugins.name('BackgroundVideo'),
      iconName('BackgroundVideo')
    ),
    type: 'Component',
  },
  render: BackgroundVideoWrapper,
  constraints: {
    children: [{is: 'BackgroundVideoPlayPauseButton', rule: 'ZeroOrOne'}],
  },
  dataType: createIntegratedRootAliasInstance(
    qualify(Plugins.name('BackgroundVideo'), typeName('BackgroundVideoWrapper'))
  ),
});

export default config;
